import { randomUUID } from 'node:crypto';
import { existsSync, mkdirSync, readFileSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';
import { regionOf, zoneLabel } from '@oxyflows/domain';
import { SyncEngine } from './sync.js';
import { sha256Hex, type Row, type Store } from './store.js';
import { OfflineBusiness } from './offline-business.js';

/**
 * The screens, answered from the device when the server cannot be reached.
 *
 * Each handler returns what the server would have returned for the same
 * request, built from the rows the device holds. Writes land in the local
 * store and the outbox in one transaction (see SyncEngine.record). What is
 * not available offline says so with 503 `offline_unavailable` rather than
 * pretending.
 */
export interface LocalRequest {
  method: string;
  path: string;
  query: URLSearchParams;
  body: unknown;
  locale: 'en' | 'ar';
}

export interface LocalResponse {
  status: number;
  body?: unknown;
  bytes?: Buffer;
  contentType?: string;
}

const T = {
  en: {
    offline: 'Not available offline. It will work again when the server can be reached.',
    noLease: 'This device has no valid offline licence. Connect to the internet and sign in once.',
    leaseExpired: 'The offline licence of this device has ended. Connect to the internet to renew it.',
    badPassword: 'The email or password is not right.',
    unknownUser: 'This person has not signed in on this computer before. Connect to the internet for the first sign-in.',
    notFound: 'Not found.',
    imageNotLocal: 'This image was not downloaded to this computer.',
    overlap: 'That time overlaps another appointment on this computer.',
  },
  ar: {
    offline: 'غير متاح دون اتصال. سيعمل مجدداً عند الوصول إلى الخادم.',
    noLease: 'لا رخصة عمل دون اتصال صالحة لهذا الجهاز. اتصل بالإنترنت وسجّل الدخول مرة واحدة.',
    leaseExpired: 'انتهت رخصة العمل دون اتصال لهذا الجهاز. اتصل بالإنترنت لتجديدها.',
    badPassword: 'البريد أو كلمة المرور غير صحيحة.',
    unknownUser: 'لم يسجّل هذا الشخص الدخول على هذا الحاسوب من قبل. اتصل بالإنترنت لأول تسجيل دخول.',
    notFound: 'غير موجود.',
    imageNotLocal: 'لم تُنزَّل هذه الصورة على هذا الحاسوب.',
    overlap: 'هذا الوقت يتداخل مع موعد آخر على هذا الحاسوب.',
  },
} as const;

const snake = (k: string) => k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
const toSnake = (o: Record<string, unknown>): Record<string, unknown> => Object.fromEntries(Object.entries(o).map(([k, v]) => [snake(k), v]));
const iso = (v: unknown): string => (v instanceof Date ? v.toISOString() : typeof v === 'string' ? (Number.isFinite(Date.parse(v)) ? new Date(v).toISOString() : v) : String(v));
const nowIso = () => new Date().toISOString();

/** `["2026-10-12 09:00:00+00","2026-10-12 09:30:00+00")` → ISO bounds. */
function period(row: Row): { startsAt: string; endsAt: string } {
  const p = String(row.period ?? '');
  const m = p.match(/^[\[(]"?([^",]+)"?,\s*"?([^")\]]+)"?[)\]]$/);
  if (m) return { startsAt: iso(m[1]!.trim()), endsAt: iso(m[2]!.trim()) };
  return { startsAt: iso(row.starts_at ?? ''), endsAt: iso(row.ends_at ?? '') };
}
const makePeriod = (startsAt: string, endsAt: string) => `["${startsAt}","${endsAt}")`;

export class OfflineApi {
  private readonly business: OfflineBusiness;

  constructor(
    private readonly engine: SyncEngine,
    private readonly dataDir: string,
  ) {
    this.business = new OfflineBusiness(engine);
  }

  private get store(): Store | null {
    return this.engine.store;
  }

  private text(locale: 'en' | 'ar', key: keyof (typeof T)['en']): string {
    return T[locale][key];
  }

  private unavailable(req: LocalRequest): LocalResponse {
    return { status: 503, body: { code: 'offline_unavailable', message: this.text(req.locale, 'offline') } };
  }

  private notFound(req: LocalRequest): LocalResponse {
    return { status: 404, body: { code: 'not_found', message: this.text(req.locale, 'notFound') } };
  }

  // -------------------------------------------------------------- routing
  async handle(req: LocalRequest): Promise<LocalResponse> {
    const { method, path } = req;
    const seg = path.split('/').filter(Boolean); // ['api', ...]
    const p = (i: number) => seg[i] ?? '';

    if (path === '/api/health' || path === '/api/health/live') return { status: 200, body: { status: 'offline' } };
    if (path === '/api/auth/login' && method === 'POST') return this.login(req);
    if (path === '/api/auth/logout' && method === 'POST') return this.logout();
    if (path === '/api/auth/me' && method === 'GET') return this.me(req);

    const store = this.store;
    const session = store?.session();
    if (!store || !session || !this.sessionAllowed()) return { status: 401, body: { code: 'unauthenticated', message: this.text(req.locale, 'noLease') } };
    const me = session.me as { user: { id: string; fullName: string; permissions: string[]; membershipId: string | null; entitlement: { features: string[] } | null }; activeClinicId: string | null };

    if (p(1) === 'dashboard' && method === 'GET') return this.dashboard(store, me);
    if (p(1) === 'patients') return this.patients(req, store, me, seg);
    if (p(1) === 'appointments') return this.appointments(req, store, me, seg);
    if (p(1) === 'clinical') return this.clinical(req, store, me, seg);
    if (p(1) === 'imaging') return this.imaging(req, store, me, seg);
    const text = (k: 'offline' | 'notFound') => this.text(req.locale, k);
    if (p(1) === 'finance') {
      const r = this.business.finance(req, store, me as never, seg, text);
      return r.status === 503 && method === 'GET' ? this.cachedOr(req, r) : r;
    }
    if (p(1) === 'inventory') {
      const r = this.business.inventory(req, store, me as never, seg, text);
      return r.status === 503 && method === 'GET' ? this.cachedOr(req, r) : r;
    }
    if (p(1) === 'lab') {
      const r = this.business.lab(req, store, me as never, seg, text);
      return r.status === 503 && method === 'GET' ? this.cachedOr(req, r) : r;
    }
    if (p(1) === 'sync' && method === 'GET') return this.cached(req);
    if (p(1) === 'clinic' || p(1) === 'dentists') {
      if (method === 'GET') return this.cached(req);
      return this.unavailable(req);
    }
    return this.unavailable(req);
  }

  private cached(req: LocalRequest): LocalResponse {
    const store = this.store;
    const key = req.path + (req.query.toString() ? `?${req.query.toString()}` : '');
    const hit = store?.cachedResponse(key) ?? store?.cachedResponse(req.path);
    if (!hit) return this.unavailable(req);
    return { status: hit.status, body: JSON.parse(hit.body) };
  }

  /** A read the device cannot build: the last online answer if there is one, else the 503. */
  private cachedOr(req: LocalRequest, fallback: LocalResponse): LocalResponse {
    const hit = this.cached(req);
    return hit.status === 503 ? fallback : { ...hit, body: { ...(hit.body as Record<string, unknown>), cached: true } };
  }

  // ----------------------------------------------------------------- auth
  private sessionAllowed(): boolean {
    const s = this.store?.session();
    if (!s) return false;
    return this.engine.leaseVerdict().ok;
  }

  private me(req: LocalRequest): LocalResponse {
    const s = this.store?.session();
    if (!s) return { status: 401, body: { code: 'unauthenticated', message: 'Signed out.' } };
    const lease = this.engine.leaseVerdict();
    if (!lease.ok) return { status: 401, body: { code: lease.level === 'expired' ? 'lease_expired' : 'lease_invalid', message: this.text(req.locale, lease.level === 'expired' ? 'leaseExpired' : 'noLease') } };
    return { status: 200, body: s.me };
  }

  private async login(req: LocalRequest): Promise<LocalResponse> {
    const body = (req.body ?? {}) as { email?: string; password?: string };
    const email = String(body.email ?? '').trim().toLowerCase();
    const password = String(body.password ?? '');
    const profile = this.engine.knownProfiles().find((p) => p.email.toLowerCase() === email);
    if (!profile) return { status: 401, body: { code: 'offline_unknown_user', message: this.text(req.locale, 'unknownUser') } };
    const store = this.engine.active && this.engine.active.userId === profile.userId && this.engine.active.clinicId === profile.clinicId && this.store ? this.store : this.engine.open(profile);
    const s = store.session();
    if (!s || !s.verifier || !SyncEngine.checkPassword(password, s.verifier)) {
      return { status: 401, body: { code: 'invalid_credentials', message: this.text(req.locale, 'badPassword') } };
    }
    const lease = this.engine.leaseVerdict();
    if (!lease.ok) return { status: 403, body: { code: lease.level === 'expired' ? 'lease_expired' : 'lease_invalid', message: this.text(req.locale, lease.level === 'expired' ? 'leaseExpired' : 'noLease') } };
    store.patchSession({ offlineSession: true });
    return { status: 200, body: s.me };
  }

  private logout(): LocalResponse {
    this.store?.patchSession({ offlineSession: false });
    return { status: 204 };
  }

  // ------------------------------------------------------------ dashboard
  private dashboard(store: Store, me: { user: { permissions: string[] } }): LocalResponse {
    const clinic = (store.rows('clinic')[0] ?? {}) as Row;
    const patients = store.rows('patient').filter((p) => p.archived_at === null || p.archived_at === undefined);
    const today = new Date();
    const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const dayEnd = dayStart + 86_400_000;
    const appts = store.rows('appointment');
    const users = this.userNames(store);
    const todays = appts
      .map((a) => ({ a, ...period(a) }))
      .filter(({ startsAt, endsAt }) => Date.parse(endsAt) > dayStart && Date.parse(startsAt) < dayEnd)
      .sort((x, y) => Date.parse(x.startsAt) - Date.parse(y.startsAt));
    const upcoming = appts.filter((a) => Date.parse(period(a).startsAt) > Date.now() && !['cancelled', 'no_show'].includes(String(a.status))).length;
    const alerts = store.rows('medical_alert').filter((a) => a.active === true).length;
    const canFinance = me.user.permissions.includes('finance.invoice.create');
    const unbilled = store.rows('treatment').filter((t) => t.status === 'completed' && t.billable === true && (t.billing_source_key === null || t.billing_source_key === undefined));
    return {
      status: 200,
      body: {
        unbilledWork: canFinance ? { count: unbilled.length, totalMinor: unbilled.reduce((s, t) => s + Number(t.price_minor ?? 0), 0) } : null,
        clinic: { nameEn: clinic.name_en ?? '', nameAr: clinic.name_ar ?? '', timezone: clinic.timezone ?? 'UTC', currency: clinic.default_currency ?? 'USD' },
        counts: { uniquePatients: patients.length, patientScope: 'all', appointmentsToday: todays.length, upcomingAppointments: upcoming, activeMedicalAlerts: alerts },
        today: todays.map(({ a, startsAt, endsAt }) => {
          const patient = a.patient_id ? store.getRow('patient', String(a.patient_id)) : null;
          return {
            id: a.id,
            status: a.status,
            reason: a.reason ?? null,
            startsAt,
            endsAt,
            dentistName: users.get(String(store.getRow('membership', String(a.dentist_membership_id))?.user_id ?? '')) ?? '',
            patient: patient ? { givenName: patient.given_name, familyName: patient.family_name, givenNameAlt: patient.given_name_alt ?? null, familyNameAlt: patient.family_name_alt ?? null } : null,
          };
        }),
        recentActivity: [],
        finance: null,
        offline: true,
      },
    };
  }

  private userNames(store: Store): Map<string, string> {
    return new Map(store.rows('app_user').map((u) => [String(u.id), String(u.fullName ?? u.full_name ?? '')]));
  }

  // ------------------------------------------------------------- patients
  private patientView(p: Row) {
    return {
      id: p.id,
      patientNumber: p.patient_number,
      givenName: p.given_name,
      familyName: p.family_name,
      givenNameAlt: p.given_name_alt ?? null,
      familyNameAlt: p.family_name_alt ?? null,
      birthDate: p.birth_date ?? null,
      sex: p.sex ?? null,
      phone: p.phone ?? null,
      preferredLocale: p.preferred_locale ?? null,
      archived: p.archived_at !== null && p.archived_at !== undefined,
      updatedAt: p.updated_at ?? null,
    };
  }

  private patientDetailView(p: Row) {
    return {
      ...this.patientView(p),
      email: p.email ?? null,
      whatsappPhone: p.whatsapp_phone ?? null,
      guardianName: p.guardian_name ?? null,
      guardianRelation: p.guardian_relation ?? null,
      guardianPhone: p.guardian_phone ?? null,
      notes: p.notes ?? null,
      version: p.version ?? 1,
      createdAt: p.created_at ?? null,
      provisional: (p as { __provisional?: boolean }).__provisional ?? false,
    };
  }

  private patients(req: LocalRequest, store: Store, me: { user: { id: string } }, seg: string[]): LocalResponse {
    const id = seg[2];
    if (req.method === 'GET' && !id) {
      const q = (req.query.get('q') ?? '').trim().toLowerCase();
      const limit = Math.min(Number(req.query.get('limit') ?? 50) || 50, 500);
      const offset = Number(req.query.get('offset') ?? 0) || 0;
      const includeArchived = req.query.get('includeArchived') === 'true';
      let list = store.rows('patient');
      if (!includeArchived) list = list.filter((p) => p.archived_at === null || p.archived_at === undefined);
      if (q) list = list.filter((p) => [p.given_name, p.family_name, p.given_name_alt, p.family_name_alt, p.patient_number, p.phone].some((v) => typeof v === 'string' && v.toLowerCase().includes(q)));
      list.sort((a, b) => String(a.family_name).localeCompare(String(b.family_name)) || String(a.given_name).localeCompare(String(b.given_name)));
      return { status: 200, body: { patients: list.slice(offset, offset + limit).map((p) => this.patientView(p)), total: list.length, scope: 'all', offline: true } };
    }
    if (req.method === 'POST' && !id) {
      const body = (req.body ?? {}) as Record<string, unknown>;
      const newId = typeof body.id === 'string' ? body.id : randomUUID();
      const numbers = store.rows('patient').map((p) => Number(String(p.patient_number ?? '').replace(/\D/g, '')) || 0);
      const row: Row = {
        ...toSnake(body),
        id: newId,
        clinic_id: this.engine.active?.clinicId,
        patient_number: `P-${Math.max(0, ...numbers) + 1}`,
        archived_at: null,
        version: 1,
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      delete row.id_;
      this.engine.record({ entity: 'patient', entityId: newId, action: 'create', payload: { ...body, id: undefined }, rows: [{ entity: 'patient', id: newId, data: row, patientId: newId }] });
      return { status: 201, body: { patient: this.patientDetailView({ ...row, __provisional: true } as Row) } };
    }
    if (!id) return this.notFound(req);
    const patient = store.getRow('patient', id);
    if (!patient) return this.notFound(req);
    if (req.method === 'GET' && seg.length === 3) {
      const history = store.rows('medical_history', { patientId: id })[0] ?? null;
      const allergies = store.rows('allergy', { patientId: id }).filter((a) => a.status === 'active');
      const alerts = store.rows('medical_alert', { patientId: id }).filter((a) => a.active === true);
      const appointments = store
        .rows('appointment', { patientId: id })
        .map((a) => ({ a, ...period(a) }))
        .sort((x, y) => Date.parse(y.startsAt) - Date.parse(x.startsAt))
        .slice(0, 20)
        .map(({ a, startsAt, endsAt }) => ({ id: a.id, status: a.status, reason: a.reason ?? null, startsAt, endsAt }));
      return {
        status: 200,
        body: {
          patient: this.patientDetailView(patient),
          medicalHistory: history ? { allergyStatus: history.allergy_status, reviewedAt: history.reviewed_at ?? null, freeText: history.free_text ?? null, medications: (history.medications as unknown[]) ?? [] } : null,
          allergies: allergies.map((a) => ({ id: a.id, substance: a.substance_text, reaction: a.reaction ?? null, severity: a.severity ?? null })),
          alerts: alerts.map((a) => ({ id: a.id, category: a.category, diagnosis: a.diagnosis_text, context: a.context ?? null, severity: a.severity ?? null })),
          appointments,
        },
      };
    }
    if (req.method === 'PATCH' && seg.length === 3) {
      const body = (req.body ?? {}) as Record<string, unknown>;
      const next: Row = { ...patient, ...toSnake(body), updated_at: nowIso() };
      delete next.__version;
      delete next.__provisional;
      this.engine.record({ entity: 'patient', entityId: id, action: 'update', baseVersion: patient.__provisional ? null : patient.__version, payload: body, rows: [{ entity: 'patient', id, data: next, patientId: id, serverVersion: patient.__version }] });
      return { status: 200, body: { patient: this.patientDetailView({ ...next, __provisional: true } as Row) } };
    }
    if (req.method === 'PATCH' && seg[3] === 'medical-history') {
      const body = (req.body ?? {}) as Record<string, unknown>;
      const existing = store.rows('medical_history', { patientId: id })[0];
      const hid = existing ? String(existing.id) : randomUUID();
      const next: Row = { ...(existing ?? { id: hid, clinic_id: this.engine.active?.clinicId, patient_id: id }), ...toSnake(body), reviewed_at: nowIso(), updated_at: nowIso() };
      delete next.__version;
      delete next.__provisional;
      this.engine.record({ entity: 'medical_history', entityId: id, action: 'update', baseVersion: existing && !existing.__provisional ? existing.__version : null, payload: body, rows: [{ entity: 'medical_history', id: hid, data: next, patientId: id, serverVersion: existing?.__version }] });
      return { status: 200, body: { updated: true } };
    }
    return this.unavailable(req);
  }

  // --------------------------------------------------------- appointments
  private appointmentView(store: Store, a: Row, users: Map<string, string>) {
    const m = store.getRow('membership', String(a.dentist_membership_id));
    const res = a.resource_id ? store.getRow('clinic_resource', String(a.resource_id)) : null;
    const patient = a.patient_id ? store.getRow('patient', String(a.patient_id)) : null;
    const { startsAt, endsAt } = period(a);
    return {
      id: a.id,
      status: a.status,
      reason: a.reason ?? null,
      provisional: a.provisional === true || (a as { __provisional?: boolean }).__provisional === true,
      startsAt,
      endsAt,
      dentistMembershipId: a.dentist_membership_id,
      dentistName: users.get(String(m?.user_id ?? '')) ?? '',
      resourceId: a.resource_id ?? null,
      resource: res ? { en: res.name_en, ar: res.name_ar } : null,
      cancelReason: a.cancel_reason ?? null,
      patient: patient ? { id: patient.id, patientNumber: patient.patient_number, givenName: patient.given_name, familyName: patient.family_name, givenNameAlt: patient.given_name_alt ?? null, familyNameAlt: patient.family_name_alt ?? null } : null,
    };
  }

  private appointments(req: LocalRequest, store: Store, me: { user: { id: string } }, seg: string[]): LocalResponse {
    const users = this.userNames(store);
    if (seg[2] === 'resources' && req.method === 'GET') {
      const dentists = store.rows('membership').filter((m) => m.is_dentist === true && (m.status === 'active' || m.status === undefined)).map((m) => ({ membershipId: m.id, name: users.get(String(m.user_id)) ?? '' }));
      const chairs = store.rows('clinic_resource').filter((r) => r.active !== false).map((r) => ({ id: r.id, en: r.name_en, ar: r.name_ar }));
      return { status: 200, body: { dentists, chairs } };
    }
    if (req.method === 'GET' && !seg[2]) {
      const from = Date.parse(req.query.get('from') ?? '') || 0;
      const to = Date.parse(req.query.get('to') ?? '') || Number.MAX_SAFE_INTEGER;
      const dentistId = req.query.get('dentistId');
      const resourceId = req.query.get('resourceId');
      const patientId = req.query.get('patientId');
      const includeCancelled = req.query.get('includeCancelled') === 'true';
      const list = store
        .rows('appointment')
        .filter((a) => {
          const { startsAt, endsAt } = period(a);
          return Date.parse(endsAt) > from && Date.parse(startsAt) < to;
        })
        .filter((a) => (!dentistId || a.dentist_membership_id === dentistId) && (!resourceId || a.resource_id === resourceId) && (!patientId || a.patient_id === patientId) && (includeCancelled || a.status !== 'cancelled'))
        .sort((x, y) => Date.parse(period(x).startsAt) - Date.parse(period(y).startsAt));
      return { status: 200, body: { appointments: list.map((a) => this.appointmentView(store, a, users)), offline: true } };
    }
    if (req.method === 'POST' && !seg[2]) {
      const body = (req.body ?? {}) as Record<string, unknown>;
      const id = typeof body.id === 'string' ? body.id : randomUUID();
      const startsAt = iso(String(body.startsAt));
      const endsAt = new Date(Date.parse(startsAt) + (Number(body.durationMinutes ?? 30) || 30) * 60_000).toISOString();
      const clash = store.rows('appointment').some((a) => {
        if (a.status === 'cancelled') return false;
        const p = period(a);
        const same = a.dentist_membership_id === body.dentistMembershipId || (body.resourceId && a.resource_id === body.resourceId);
        return same && Date.parse(p.startsAt) < Date.parse(endsAt) && Date.parse(p.endsAt) > Date.parse(startsAt);
      });
      if (clash) return { status: 409, body: { code: 'slot_taken', message: this.text(req.locale, 'overlap') } };
      const row: Row = {
        id,
        clinic_id: this.engine.active?.clinicId,
        patient_id: body.patientId ?? null,
        dentist_membership_id: body.dentistMembershipId,
        resource_id: body.resourceId ?? null,
        period: makePeriod(startsAt, endsAt),
        status: 'scheduled',
        provisional: true,
        reason: body.reason ?? null,
        notes: body.notes ?? null,
        cancel_reason: null,
        booked_by: me.user.id,
        version: 1,
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      this.engine.record({ entity: 'appointment', entityId: id, action: 'create', payload: { ...body, id: undefined }, rows: [{ entity: 'appointment', id, data: row, patientId: (body.patientId as string | null) ?? null }] });
      return { status: 201, body: { appointment: this.appointmentView(store, { ...row, __provisional: true } as Row, users), provisional: true } };
    }
    const id = seg[2];
    if (!id) return this.notFound(req);
    const appt = store.getRow('appointment', id);
    if (!appt) return this.notFound(req);
    if (req.method === 'POST' && seg[3] === 'status') {
      const body = (req.body ?? {}) as Record<string, unknown>;
      const next: Row = { ...appt, status: body.status, cancel_reason: body.reason ?? appt.cancel_reason ?? null, updated_at: nowIso() };
      delete next.__version;
      delete next.__provisional;
      this.engine.record({ entity: 'appointment', entityId: id, action: 'update', baseVersion: appt.__provisional ? null : appt.__version, payload: body, rows: [{ entity: 'appointment', id, data: next, patientId: (appt.patient_id as string | null) ?? null, serverVersion: appt.__version }] });
      return { status: 200, body: { appointment: this.appointmentView(store, next, users) } };
    }
    if (req.method === 'PATCH' && seg.length === 3) {
      const body = (req.body ?? {}) as Record<string, unknown>;
      const cur = period(appt);
      const startsAt = body.startsAt ? iso(String(body.startsAt)) : cur.startsAt;
      const minutes = Number(body.durationMinutes ?? (Date.parse(cur.endsAt) - Date.parse(cur.startsAt)) / 60_000) || 30;
      const endsAt = new Date(Date.parse(startsAt) + minutes * 60_000).toISOString();
      const next: Row = { ...appt, period: makePeriod(startsAt, endsAt), dentist_membership_id: body.dentistMembershipId ?? appt.dentist_membership_id, resource_id: body.resourceId ?? appt.resource_id ?? null, provisional: true, updated_at: nowIso() };
      delete next.__version;
      delete next.__provisional;
      this.engine.record({ entity: 'appointment', entityId: id, action: 'update', baseVersion: appt.__provisional ? null : appt.__version, payload: body, rows: [{ entity: 'appointment', id, data: next, patientId: (appt.patient_id as string | null) ?? null, serverVersion: appt.__version }] });
      return { status: 200, body: { appointment: this.appointmentView(store, next, users) } };
    }
    return this.unavailable(req);
  }

  // ------------------------------------------------------------- clinical
  private treatmentView(r: Row) {
    return {
      id: r.id,
      patientId: r.patient_id,
      nameEn: r.name_en,
      nameAr: r.name_ar,
      toothFdi: r.tooth_fdi ?? null,
      surfaces: (r.surfaces as string[]) ?? [],
      zone: r.zone ?? null,
      doseQty: r.dose_qty === null || r.dose_qty === undefined ? null : Number(r.dose_qty),
      doseUnit: r.dose_unit ?? null,
      productLabel: r.product_label ?? null,
      lotNumber: r.lot_number ?? null,
      status: r.status,
      priceMinor: Number(r.price_minor ?? 0),
      currency: r.currency,
      notes: r.notes ?? null,
      billable: r.billable !== false,
      billed: r.billing_source_key !== null && r.billing_source_key !== undefined,
      completedAt: r.completed_at ?? null,
      createdAt: r.created_at,
      dentistMembershipId: r.dentist_membership_id ?? null,
      provisional: (r as { __provisional?: boolean }).__provisional ?? false,
    };
  }

  private clinical(req: LocalRequest, store: Store, me: { user: { id: string; fullName: string; membershipId: string | null } }, seg: string[]): LocalResponse {
    const sub = seg[2];
    const byRecency = (a: Row, b: Row) => Date.parse(String(b.effective_at ?? '')) - Date.parse(String(a.effective_at ?? '')) || Date.parse(String(b.recorded_at ?? '')) - Date.parse(String(a.recorded_at ?? ''));

    if (sub === 'catalogue' && req.method === 'GET') {
      const specialty = req.query.get('specialty');
      const items = store
        .rows('treatment_catalogue')
        .filter((c) => c.active !== false && (!specialty || c.specialty === specialty))
        .sort((a, b) => String(a.category ?? '').localeCompare(String(b.category ?? '')) || String(a.name_en).localeCompare(String(b.name_en)))
        .map((r) => ({
          id: r.id, code: r.code, nameEn: r.name_en, nameAr: r.name_ar, category: r.category, priceMinor: Number(r.default_price_minor ?? 0), currency: r.currency,
          durationMinutes: r.duration_minutes ?? null, requiresTooth: r.requires_tooth === true, impliesLab: r.implies_lab === true, resultingToothStatus: r.resulting_tooth_status ?? null,
          specialty: r.specialty ?? 'dental', requiresZone: r.requires_zone === true, doseUnit: r.dose_unit ?? null, zoneMark: r.zone_mark ?? null,
        }));
      return { status: 200, body: { items } };
    }

    if (sub === 'chart') {
      const patientId = seg[3];
      if (!patientId) return this.notFound(req);
      const patient = store.getRow('patient', patientId);
      if (!patient) return this.notFound(req);
      if (req.method === 'GET') {
        const observations = store.rows('tooth_observation', { patientId }).sort(byRecency);
        const surfaces = store.rows('surface_finding', { patientId }).sort(byRecency);
        const treatments = store.rows('treatment', { patientId }).sort((a, b) => Date.parse(String(b.created_at)) - Date.parse(String(a.created_at)));
        const links = store.rows('attachment_link', { patientId }).filter((l) => l.tooth_fdi);
        const currentTooth = new Map<string, Row>();
        for (const o of observations) if (!currentTooth.has(String(o.tooth_fdi))) currentTooth.set(String(o.tooth_fdi), o);
        const currentSurface = new Map<string, Row>();
        for (const s of surfaces) {
          const key = `${s.tooth_fdi}:${s.surface}`;
          if (!currentSurface.has(key)) currentSurface.set(key, s);
        }
        return {
          status: 200,
          body: {
            patient: { id: patient.id, birthDate: patient.birth_date ?? null, givenName: patient.given_name, familyName: patient.family_name, givenNameAlt: patient.given_name_alt ?? null, familyNameAlt: patient.family_name_alt ?? null },
            teeth: [...currentTooth.values()].map((o) => ({ toothFdi: o.tooth_fdi, status: o.status, kind: o.kind, note: o.note ?? null, effectiveAt: o.effective_at, dentition: o.dentition })),
            surfaces: [...currentSurface.values()].map((s) => ({ toothFdi: s.tooth_fdi, surface: s.surface, finding: s.finding, note: s.note ?? null, effectiveAt: s.effective_at })),
            treatments: treatments.map((t) => this.treatmentView(t)),
            images: links.map((l) => {
              const a = (store.getRow('attachment', String(l.attachment_id)) ?? {}) as Row;
              return { linkId: l.id, attachmentId: l.attachment_id, toothFdi: l.tooth_fdi, surfaces: (l.surfaces as string[]) ?? [], kind: a.kind, mimeType: a.mime_type, source: a.source, isSynthetic: a.is_synthetic === true, at: a.acquired_at ?? a.uploaded_at };
            }),
            history: {
              observations: observations.map((o) => ({ id: o.id, toothFdi: o.tooth_fdi, status: o.status, kind: o.kind, note: o.note ?? null, effectiveAt: o.effective_at })),
              surfaces: surfaces.map((s) => ({ id: s.id, toothFdi: s.tooth_fdi, surface: s.surface, finding: s.finding, effectiveAt: s.effective_at })),
            },
          },
        };
      }
      if (req.method === 'POST' && seg[4] === 'observation') {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const id = randomUUID();
        const fdi = String(body.toothFdi ?? '');
        const row: Row = { id, clinic_id: this.engine.active?.clinicId, patient_id: patientId, tooth_fdi: fdi, status: body.status, kind: body.kind ?? 'finding', note: body.note ?? null, dentition: /^[5-8]/.test(fdi) ? 'primary' : 'permanent', effective_at: body.effectiveAt ? iso(String(body.effectiveAt)) : nowIso(), recorded_at: nowIso(), recorded_by: me.user.id };
        this.engine.record({ entity: 'tooth_observation', entityId: id, action: 'append', payload: { ...body, patientId }, rows: [{ entity: 'tooth_observation', id, data: row, patientId }] });
        return { status: 201, body: { id } };
      }
      if (req.method === 'POST' && seg[4] === 'surfaces') {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const opId = randomUUID();
        const rows = ((body.surfaces as string[]) ?? []).map((surface) => {
          const id = randomUUID();
          return { entity: 'surface_finding', id, patientId, data: { id, clinic_id: this.engine.active?.clinicId, patient_id: patientId, tooth_fdi: body.toothFdi, surface, finding: body.finding, note: body.note ?? null, effective_at: nowIso(), recorded_at: nowIso(), recorded_by: me.user.id } as Row };
        });
        this.engine.record({ entity: 'surface_finding', entityId: opId, action: 'append', payload: { ...body, patientId }, rows });
        return { status: 201, body: { ids: rows.map((r) => r.id) } };
      }
    }

    if (sub === 'zones') {
      const patientId = seg[3];
      if (!patientId || !store.getRow('patient', patientId)) return this.notFound(req);
      if (req.method === 'GET') {
        const rows = store.rows('zone_observation', { patientId }).sort(byRecency);
        const treatments = store.rows('treatment', { patientId }).filter((t) => t.zone).sort((a, b) => Date.parse(String(b.created_at)) - Date.parse(String(a.created_at)));
        const links = store.rows('attachment_link', { patientId }).filter((l) => l.zone);
        const withdrawn = new Set(rows.filter((r) => r.kind === 'correction' && r.supersedes_id).map((r) => String(r.supersedes_id)));
        const current = new Map<string, Row>();
        for (const r of rows) if ((r.kind === 'finding' || r.kind === 'existing') && !withdrawn.has(String(r.id)) && !current.has(String(r.zone))) current.set(String(r.zone), r);
        const detail = (v: unknown) => (typeof v === 'string' ? (JSON.parse(v) as Row) : ((v ?? {}) as Row));
        return {
          status: 200,
          body: {
            zones: [...current.values()].map((r) => ({ zone: r.zone, region: r.region, finding: r.finding ?? null, severity: r.severity ?? null, kind: r.kind, note: r.note ?? null, effectiveAt: r.effective_at })),
            procedures: rows.filter((r) => r.kind === 'procedure' && !withdrawn.has(String(r.id))).map((r) => ({ id: r.id, zone: r.zone, region: r.region, mark: r.mark ?? null, detail: detail(r.detail), note: r.note ?? null, treatmentId: r.treatment_id ?? null, effectiveAt: r.effective_at })),
            treatments: treatments.map((t) => this.treatmentView(t)),
            images: links.map((l) => {
              const a = (store.getRow('attachment', String(l.attachment_id)) ?? {}) as Row;
              return { linkId: l.id, attachmentId: l.attachment_id, zone: l.zone, kind: a.kind, mimeType: a.mime_type, isSynthetic: a.is_synthetic === true, at: a.acquired_at ?? a.uploaded_at };
            }),
            history: rows.map((r) => ({ id: r.id, zone: r.zone, region: r.region, kind: r.kind, finding: r.finding ?? null, severity: r.severity ?? null, mark: r.mark ?? null, detail: detail(r.detail), note: r.note ?? null, supersedesId: r.supersedes_id ?? null, effectiveAt: r.effective_at })),
          },
        };
      }
      if (req.method === 'POST' && seg[4] === 'observation') {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const id = randomUUID();
        const zone = String(body.zone ?? '');
        const row: Row = { id, clinic_id: this.engine.active?.clinicId, patient_id: patientId, region: regionOf(zone), zone, kind: body.kind ?? 'finding', finding: body.finding ?? null, severity: body.severity ?? null, mark: null, detail: {}, note: body.note ?? null, effective_at: body.effectiveAt ? iso(String(body.effectiveAt)) : nowIso(), recorded_at: nowIso(), recorded_by: me.user.id, supersedes_id: null };
        this.engine.record({ entity: 'zone_observation', entityId: id, action: 'append', payload: { ...body, patientId }, rows: [{ entity: 'zone_observation', id, data: row, patientId }] });
        return { status: 201, body: { id, zone: zoneLabel(zone) } };
      }
    }

    if (sub === 'treatments') {
      const id = seg[3];
      if (req.method === 'GET' && !id) {
        const patientId = req.query.get('patientId');
        const list = (patientId ? store.rows('treatment', { patientId }) : store.rows('treatment')).sort((a, b) => Date.parse(String(b.created_at)) - Date.parse(String(a.created_at))).slice(0, 200);
        return {
          status: 200,
          body: {
            treatments: list.map((t) => {
              const p = store.getRow('patient', String(t.patient_id));
              return { ...this.treatmentView(t), patient: { patientNumber: p?.patient_number ?? null, givenName: p?.given_name ?? null, familyName: p?.family_name ?? null } };
            }),
          },
        };
      }
      if (req.method === 'POST' && !id) {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const tid = typeof body.id === 'string' ? body.id : randomUUID();
        const patientId = String(body.patientId ?? '');
        if (!store.getRow('patient', patientId)) return this.notFound(req);
        const completed = body.status === 'completed';
        const row: Row = {
          id: tid, clinic_id: this.engine.active?.clinicId, patient_id: patientId, catalogue_id: body.catalogueId ?? null, name_en: body.nameEn, name_ar: body.nameAr,
          tooth_fdi: body.toothFdi ?? null, surfaces: (body.surfaces as string[]) ?? [], zone: body.zone ?? null, dose_qty: body.doseQty ?? null, dose_unit: body.doseUnit ?? null,
          product_label: body.productLabel ?? null, lot_number: body.lotNumber ?? null, status: body.status ?? 'planned', price_minor: Number(body.priceMinor ?? 0), currency: body.currency ?? 'USD',
          notes: body.notes ?? null, billable: body.billable !== false, billing_source_key: null, completed_at: completed ? (body.performedAt ? iso(String(body.performedAt)) : nowIso()) : null,
          dentist_membership_id: body.dentistMembershipId ?? me.user.membershipId ?? null, version: 1, created_at: nowIso(), updated_at: nowIso(),
        };
        this.engine.record({ entity: 'treatment', entityId: tid, action: 'create', payload: { ...body, id: undefined }, rows: [{ entity: 'treatment', id: tid, data: row, patientId }] });
        return { status: 201, body: { treatment: this.treatmentView({ ...row, __provisional: true } as Row), toothChartedAs: null, provisional: true } };
      }
      if (!id) return this.notFound(req);
      const t = store.getRow('treatment', id);
      if (!t) return this.notFound(req);
      const patientId = String(t.patient_id);
      if (req.method === 'PATCH' && seg[4] === 'status') {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const completed = body.status === 'completed';
        const next: Row = { ...t, status: body.status, completed_at: completed ? (body.performedAt ? iso(String(body.performedAt)) : nowIso()) : null, updated_at: nowIso() };
        delete next.__version;
        delete next.__provisional;
        this.engine.record({ entity: 'treatment', entityId: id, action: 'update', baseVersion: t.__provisional ? null : t.__version, payload: body, rows: [{ entity: 'treatment', id, data: next, patientId, serverVersion: t.__version }] });
        return { status: 200, body: { treatment: this.treatmentView(next), toothChartedAs: null } };
      }
      if (req.method === 'PATCH' && seg.length === 4) {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const next: Row = { ...t, ...toSnake(body), updated_at: nowIso() };
        delete next.__version;
        delete next.__provisional;
        this.engine.record({ entity: 'treatment', entityId: id, action: 'update', baseVersion: t.__provisional ? null : t.__version, payload: body, rows: [{ entity: 'treatment', id, data: next, patientId, serverVersion: t.__version }] });
        return { status: 200, body: { treatment: this.treatmentView(next) } };
      }
      if (req.method === 'DELETE' && seg.length === 4) {
        const body = (req.body ?? {}) as Record<string, unknown>;
        if (t.billing_source_key) return { status: 409, body: { code: 'billed', message: 'Billed work cannot be removed.' } };
        this.engine.record({ entity: 'treatment', entityId: id, action: 'delete', baseVersion: t.__provisional ? null : t.__version, payload: body, rows: [], deletes: [{ entity: 'treatment', id }] });
        return { status: 200, body: { removed: true } };
      }
    }

    if (sub === 'notes') {
      const idOrPatient = seg[3];
      if (req.method === 'GET' && idOrPatient) {
        const notes = store.rows('clinical_note', { patientId: idOrPatient }).sort((a, b) => Date.parse(String(b.occurred_at)) - Date.parse(String(a.occurred_at))).slice(0, 100);
        const amendments = store.rows('note_amendment');
        return {
          status: 200,
          body: {
            notes: notes.map((n) => ({
              id: n.id, body: n.body, toothFdi: n.tooth_fdi ?? null, finalised: n.finalised_at !== null && n.finalised_at !== undefined, author: n.author_label ?? null, occurredAt: n.occurred_at,
              amendments: amendments.filter((a) => a.note_id === n.id).map((a) => ({ id: a.id, body: a.body, reason: a.reason, author: a.author_label ?? null, at: a.created_at })),
            })),
          },
        };
      }
      if (req.method === 'POST' && !idOrPatient) {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const id = typeof body.id === 'string' ? body.id : randomUUID();
        const patientId = String(body.patientId ?? '');
        if (!store.getRow('patient', patientId)) return this.notFound(req);
        const row: Row = { id, clinic_id: this.engine.active?.clinicId, patient_id: patientId, visit_id: body.visitId ?? null, treatment_id: body.treatmentId ?? null, tooth_fdi: body.toothFdi ?? null, body: body.body, author_id: me.user.id, author_label: me.user.fullName, occurred_at: nowIso(), finalised_at: body.finalise ? nowIso() : null, created_at: nowIso() };
        this.engine.record({ entity: 'clinical_note', entityId: id, action: 'append', payload: { ...body, id: undefined }, rows: [{ entity: 'clinical_note', id, data: row, patientId }] });
        return { status: 201, body: { id } };
      }
    }

    if (sub === 'timeline' && req.method === 'GET' && seg[3]) {
      const patientId = seg[3];
      const events: { at: string; kind: string; title: string; detail: string | null; actor: string | null }[] = [];
      for (const t of store.rows('treatment', { patientId })) {
        const dose = t.dose_qty !== null && t.dose_qty !== undefined ? ` · ${Number(t.dose_qty)} ${t.dose_unit ?? ''}`.trimEnd() : '';
        events.push({ at: iso(t.completed_at ?? t.created_at), kind: 'treatment', title: String(t.name_en), detail: t.tooth_fdi ? `Tooth ${t.tooth_fdi} · ${t.status}` : t.zone ? `${zoneLabel(String(t.zone))}${dose} · ${t.status}` : String(t.status), actor: null });
      }
      for (const a of store.rows('appointment', { patientId })) events.push({ at: period(a).startsAt, kind: 'appointment', title: String(a.reason ?? 'Appointment'), detail: String(a.status), actor: null });
      for (const n of store.rows('clinical_note', { patientId })) events.push({ at: iso(n.occurred_at), kind: 'note', title: n.finalised_at ? 'Clinical note' : 'Draft note', detail: String(n.body).slice(0, 160), actor: (n.author_label as string | null) ?? null });
      for (const i of store.rows('attachment', { patientId })) events.push({ at: iso(i.acquired_at ?? i.uploaded_at), kind: 'image', title: String(i.kind), detail: i.is_synthetic ? 'simulated' : null, actor: null });
      events.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
      return { status: 200, body: { events, offline: true } };
    }
    return this.unavailable(req);
  }

  // -------------------------------------------------------------- imaging
  private blobsDir(): string {
    const d = join(this.dataDir, 'blobs');
    mkdirSync(d, { recursive: true });
    return d;
  }

  private imaging(req: LocalRequest, store: Store, me: { user: { id: string } }, seg: string[]): LocalResponse {
    const sub = seg[2];
    if (sub === 'patients' && seg[3] && req.method === 'GET') {
      const patientId = seg[3];
      const tooth = req.query.get('tooth');
      const links = store.rows('attachment_link', { patientId });
      const images = store
        .rows('attachment', { patientId })
        .sort((a, b) => Date.parse(String(b.uploaded_at ?? b.created_at)) - Date.parse(String(a.uploaded_at ?? a.created_at)))
        .map((a) => ({
          id: a.id, kind: a.kind, modality: a.modality ?? null, mimeType: a.mime_type, byteSize: Number(a.byte_size ?? 0), source: a.source ?? 'upload', isSynthetic: a.is_synthetic === true,
          scanState: a.scan_state ?? 'clean', transferState: store.blob(String(a.id)) ? ((a as { __provisional?: boolean }).__provisional ? 'local_only' : 'verified') : 'remote_only', at: a.acquired_at ?? a.uploaded_at ?? a.created_at,
          annotations: a.annotations ?? {}, available: !!store.blob(String(a.id)),
          links: links.filter((l) => l.attachment_id === a.id).map((l) => ({ linkId: l.id, toothFdi: l.tooth_fdi ?? null, zone: l.zone ?? null, surfaces: (l.surfaces as string[]) ?? [], note: l.note ?? null })),
        }))
        .filter((i) => !tooth || i.links.some((l) => l.toothFdi === tooth));
      return { status: 200, body: { images, offline: true } };
    }
    if (sub === 'file' && seg[3] && req.method === 'GET') {
      const b = store.blob(seg[3]);
      if (!b || !existsSync(b.localPath)) return { status: 404, body: { code: 'not_available_offline', message: this.text(req.locale, 'imageNotLocal') } };
      return { status: 200, bytes: readFileSync(b.localPath), contentType: b.mimeType };
    }
    if (sub === 'upload' && req.method === 'POST') {
      const body = (req.body ?? {}) as Record<string, unknown>;
      const id = typeof body.id === 'string' ? body.id : randomUUID();
      const patientId = String(body.patientId ?? '');
      if (!store.getRow('patient', patientId)) return this.notFound(req);
      let bytes: Buffer;
      try {
        bytes = Buffer.from(String(body.data ?? ''), 'base64');
      } catch {
        return { status: 400, body: { code: 'bad_encoding', message: 'The file data is not valid base64.' } };
      }
      if (bytes.length === 0) return { status: 400, body: { code: 'validation_failed', message: 'The file is empty.' } };
      const mime = String(body.mimeType ?? 'application/octet-stream');
      const ext = mime === 'image/png' ? 'png' : mime === 'image/jpeg' ? 'jpg' : 'bin';
      const localPath = join(this.blobsDir(), `${id}.${ext}`);
      writeFileSync(localPath, bytes);
      const sha = sha256Hex(bytes);
      const source = body.source === 'sensor' || body.source === 'simulator' || body.source === 'import' ? String(body.source) : 'upload';
      const attachment: Row = { id, clinic_id: this.engine.active?.clinicId, patient_id: patientId, kind: body.kind, modality: body.kind === 'cbct' ? 'CBCT' : null, mime_type: mime, byte_size: bytes.length, sha256: sha, source, is_synthetic: source === 'simulator', scan_state: 'clean', transfer_state: 'local_only', acquired_at: body.acquiredAt ?? null, uploaded_at: nowIso(), uploaded_by: me.user.id, annotations: body.annotations ?? {}, version: 1, created_at: nowIso(), updated_at: nowIso() };
      const rows: { entity: string; id: string; data: Row; patientId: string }[] = [{ entity: 'attachment', id, data: attachment, patientId }];
      if (body.toothFdi || body.zone) {
        const linkId = randomUUID();
        rows.push({ entity: 'attachment_link', id: linkId, data: { id: linkId, clinic_id: this.engine.active?.clinicId, attachment_id: id, patient_id: patientId, tooth_fdi: body.toothFdi ?? null, surfaces: (body.surfaces as string[]) ?? [], zone: body.zone ?? null, note: body.note ?? null, linked_at: nowIso(), linked_by: me.user.id }, patientId });
      }
      store.putBlob({ attachmentId: id, mimeType: mime, byteSize: bytes.length, sha256: sha, localPath, origin: 'captured' });
      this.engine.record({ entity: 'attachment', entityId: id, action: 'create', payload: { ...body, id: undefined }, rows });
      return { status: 201, body: { id, mimeType: mime, byteSize: bytes.length, viewerSupported: mime !== 'application/dicom', viewerNote: null, provisional: true } };
    }
    const id = seg[2];
    if (id && seg[3] === 'annotations') {
      const a = store.getRow('attachment', id);
      if (!a) return this.notFound(req);
      if (req.method === 'GET') return { status: 200, body: { annotations: a.annotations ?? {} } };
      if (req.method === 'PUT') {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const next: Row = { ...a, annotations: body, updated_at: nowIso() };
        delete next.__version;
        delete next.__provisional;
        this.engine.record({ entity: 'attachment', entityId: id, action: 'update', baseVersion: a.__provisional ? null : a.__version, payload: body, rows: [{ entity: 'attachment', id, data: next, patientId: (a.patient_id as string | null) ?? null, serverVersion: a.__version }] });
        return { status: 200, body: { annotations: body } };
      }
    }
    return this.unavailable(req);
  }
}
